import { PlusOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { Space } from '@client/components/_ui/layout/Space';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useOrg } from '@client/hooks/business/org/useOrg';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, usePrismaTable } from '@client/hooks/table-v2';
import { useFormatter } from '@client/hooks/utils/useFormatter';
import { type RouterInputs, type RouterOutputs, trpc } from '@client/trpc/client';
import { DEFAULT_PAGE_SIZE } from '@shared/constants';
import {
    ANALYSIS_STEP_LABELS,
    COMPLIANCE_SCAN_STATUS_LABELS,
    type ComplianceScanProgress,
    type ComplianceScanType,
    SCAN_TYPE_LABELS,
} from '@shared/types';
import { createFileRoute } from '@tanstack/react-router';
import { Tag, Tooltip, Typography } from 'antd';
import { Eye, FileCheck, RefreshCw, Shield, XCircle } from 'lucide-react';
import { useState } from 'react';
import { type ComplianceCheckAction, ComplianceCheckDrawer } from './ComplianceCheckDrawer';
import { type ReAnalysisChoice, ReAnalysisModal, type ReAnalysisModalAction } from './ReAnalysisModal';
import { type ViewScrapedDataAction, ViewScrapedDataDrawer } from './ViewScrapedDataDrawer';
import { WebsiteCheckerFilters } from './WebsiteCheckerFilters';

export const Route = createFileRoute('/console/$orgId/tools/website-checker/')({
    staticData: {
        ui: {
            title: 'Website checker',
            subtitle: 'Scan and analyse websites for compliance',
            widthVariant: 'default',
        },
        modules: ['website-checker'],
        permissions: ['website_checker:access'],
    },
    component: ComplianceWebsiteChecker,
});

type QueryParams = RouterInputs['websiteChecker']['listScrapedData'];
type ScrapedDataRow = RouterOutputs['websiteChecker']['listScrapedData']['rows'][number];
type ScrapedDataOrderBy = NonNullable<QueryParams['orderBy']>;

function getTimeSinceLastScan(date: Date) {
    const now = new Date();
    const diff = now.getTime() - new Date(date).getTime();
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

    if (days > 0) {
        return `${days}d ${hours}h ago`;
    }
    return `${hours}h ago`;
}

function ComplianceWebsiteChecker() {
    const { formatDateTime } = useFormatter();
    const navigate = useNavigate();
    const org = useOrg();
    const { trpcUtils } = useTrpc();
    const [complianceCheckAction, setComplianceCheckAction] = useState<ComplianceCheckAction>(['none']);
    const [viewDrawerAction, setViewDrawerAction] = useState<ViewScrapedDataAction>(['none']);
    const [reAnalysisModalAction, setReAnalysisModalAction] = useState<ReAnalysisModalAction>(['none']);
    const [reuseScrapedData, setReuseScrapedData] = useState(false);

    const { mutate: deleteScrapedData } = trpc.websiteChecker.deleteScrapedData.useMutation();

    const cancelMutation = trpc.websiteChecker.cancelScan.useMutation({
        onSuccess: () => {
            message.success('Scan cancelled');
            trpcUtils.websiteChecker.listScrapedData.invalidate();
        },
        onError: (error) => {
            errorMessage.show(error);
        },
    });

    const [queryParams, setQueryParams] = usePathState<QueryParams>('query-params', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: { updatedAt: 'desc' },
        where: {},
    });

    const { data, isPending, refetch } = trpc.websiteChecker.listScrapedData.useQuery(queryParams, {
        refetchInterval: (query) => {
            const rows = query.state.data?.rows;
            const hasInProgressScan = rows?.some(
                (row) =>
                    row.complianceScanStatus && ['PENDING', 'SCRAPING', 'ANALYZING'].includes(row.complianceScanStatus),
            );
            return hasInProgressScan ? 3000 : false; // Poll every 3s when scans in progress
        },
    });

    const columnHelper = createColumnHelper<ScrapedDataRow, ScrapedDataOrderBy>();

    const columns = [
        columnHelper.accessor('url', {
            id: 'url',
            header: 'URL',
            sortable: 'url',
            cell: ({ row }) => (
                <div>
                    <Typography.Text ellipsis style={{ maxWidth: 300 }}>
                        <Tooltip title={row.url}>
                            <a
                                href={row.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                            >
                                {row.url}
                            </a>
                        </Tooltip>
                    </Typography.Text>
                    {row.companyName && (
                        <Typography.Text type="secondary" className="block text-xs" ellipsis>
                            {row.companyName}
                        </Typography.Text>
                    )}
                </div>
            ),
            hideable: false,
        }),
        columnHelper.display({
            id: 'scanType',
            header: 'Scan type',
            cell: ({ row }) =>
                row.scanType ? (
                    <Tag variant="outlined">{SCAN_TYPE_LABELS[row.scanType as ComplianceScanType]}</Tag>
                ) : null,
            width: 120,
        }),
        columnHelper.accessor('status', {
            id: 'status',
            header: 'Status',
            sortable: 'status',
            cell: ({ row }) => {
                // If a compliance scan exists, always show its status
                if (row.complianceScanStatus) {
                    const statusLabel = COMPLIANCE_SCAN_STATUS_LABELS[row.complianceScanStatus];
                    const progress = row.complianceScanProgress as ComplianceScanProgress | null;
                    const isInProgress = ['PENDING', 'SCRAPING', 'ANALYZING'].includes(row.complianceScanStatus);

                    // Show progress details for in-progress scans
                    let progressDetail = '';
                    if (isInProgress) {
                        if (progress?.scrapingProgress && row.complianceScanStatus === 'SCRAPING') {
                            const { pagesScraped } = progress.scrapingProgress;
                            progressDetail = ` (${pagesScraped} pages)`;
                        } else if (progress?.analysisProgress && row.complianceScanStatus === 'ANALYZING') {
                            const { currentStep, rulesChecked, totalRules } = progress.analysisProgress;

                            if (
                                currentStep &&
                                currentStep !== 'ANALYZING' &&
                                currentStep !== 'COMPLETED' &&
                                currentStep !== 'ERROR'
                            ) {
                                progressDetail = ` (${ANALYSIS_STEP_LABELS[currentStep]})`;
                            } else if (currentStep === 'ANALYZING' && totalRules && totalRules > 0) {
                                progressDetail = ` (${rulesChecked ?? 0}/${totalRules} rules)`;
                            }
                        }
                    }

                    return (
                        <StatusBadge
                            module="complianceScan"
                            status={row.complianceScanStatus}
                            customLabel={`${statusLabel}${progressDetail}`}
                        />
                    );
                }

                // Fallback: no compliance scan yet, so show the page download status
                return <StatusBadge module="scrapedData" status={row.status} />;
            },
            width: 200,
        }),
        columnHelper.display({
            id: 'pagesScraped',
            header: 'Pages scanned',
            cell: ({ row }) => {
                // Show "..." during active scraping
                if (row.complianceScanStatus === 'SCRAPING') {
                    return <Tag variant="outlined">...</Tag>;
                }
                return <Tag variant="outlined">{row.pagesScraped}</Tag>;
            },
            width: 130,
            align: 'center',
        }),
        columnHelper.accessor('updatedAt', {
            id: 'updatedAt',
            header: 'Last scan',
            sortable: 'updatedAt',
            cell: ({ row }) => (
                <Tooltip title={formatDateTime(row.updatedAt)}>{getTimeSinceLastScan(row.updatedAt)}</Tooltip>
            ),
            width: 150,
        }),
        columnHelper.accessor('error', {
            id: 'error',
            header: 'Error',
            cell: ({ row }) =>
                row.error ? (
                    <Tooltip title={row.error}>
                        <Typography.Text type="danger" ellipsis style={{ maxWidth: 200 }}>
                            {row.error}
                        </Typography.Text>
                    </Tooltip>
                ) : null,
            width: 200,
        }),
        columnHelper.actions({
            width: 200,
            cell: ({ row }) => {
                const isComplianceInProgress =
                    row.complianceScanStatus !== null &&
                    ['PENDING', 'SCRAPING', 'ANALYZING'].includes(row.complianceScanStatus);
                const complianceScanId = row.complianceScanId;

                return (
                    <Space size="small" className="flex justify-end">
                        {row.complianceScanStatus && (
                            <Button.Icon
                                icon={<FileCheck />}
                                color="blue"
                                size="small"
                                tooltip="View compliance report"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    navigate({
                                        to: '/console/$orgId/tools/website-checker/report/$id',
                                        params: { orgId: org.id, id: row.id },
                                    });
                                }}
                            />
                        )}
                        <Button.Icon
                            icon={<Eye />}
                            color="blue"
                            size="small"
                            tooltip="View scraped data"
                            onClick={(e) => {
                                e.stopPropagation();
                                setViewDrawerAction(['view', row.id]);
                            }}
                        />
                        {isComplianceInProgress && complianceScanId !== null && (
                            <Button.Icon
                                icon={<XCircle />}
                                color="red"
                                size="small"
                                tooltip="Cancel scan"
                                loading={
                                    cancelMutation.isPending && cancelMutation.variables?.scanId === complianceScanId
                                }
                                onClick={(e) => {
                                    e.stopPropagation();
                                    cancelMutation.mutate({ scanId: complianceScanId });
                                }}
                            />
                        )}
                        {row.status === 'SUCCESS' && (
                            <Button.Icon
                                icon={<Shield />}
                                color="green"
                                size="small"
                                tooltip="Analyse compliance"
                                disabled={isComplianceInProgress}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setReAnalysisModalAction([
                                        'choose',
                                        {
                                            scrapedDataId: row.id,
                                            websiteUrl: row.url,
                                            lastScrapedAt: row.updatedAt,
                                        },
                                    ]);
                                }}
                            />
                        )}
                        <Button.Icon
                            icon={<RefreshCw />}
                            color="cyan"
                            size="small"
                            tooltip="Re-scan"
                            disabled={isComplianceInProgress}
                            onClick={(e) => {
                                e.stopPropagation();
                                // Open unified compliance check drawer for re-scanning
                                setReuseScrapedData(false);
                                setComplianceCheckAction([
                                    're-analyse',
                                    { scrapedDataId: row.id, websiteUrl: row.url },
                                ]);
                            }}
                        />
                        <Button.Delete
                            disabled={isComplianceInProgress}
                            onConfirm={(callback) =>
                                deleteScrapedData(
                                    { id: row.id },
                                    {
                                        onSuccess() {
                                            callback();
                                            trpcUtils.websiteChecker.listScrapedData.invalidate();
                                        },
                                        onError: (error) => {
                                            errorMessage.show(error);
                                        },
                                    },
                                )
                            }
                        />
                    </Space>
                );
            },
        }),
    ];

    const tableProps = usePrismaTable<ScrapedDataRow, QueryParams['where'], ScrapedDataOrderBy>({
        data: data?.rows ?? [],
        total: data?.total ?? 0,
        loading: isPending,
        columns,
        rowKey: 'id',
        queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'website-checker-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        emptyState: { noRecordsTitle: 'No compliance checks yet' },
    });

    return (
        <PageController route={Route}>
            <TableToolbar
                filters={
                    <WebsiteCheckerFilters
                        onChange={(where) => setQueryParams((prev) => ({ ...prev, where, offset: 0 }))}
                    />
                }
                actions={
                    <Button icon={<PlusOutlined />} onClick={() => setComplianceCheckAction(['new'])} type="primary">
                        New compliance check
                    </Button>
                }
            />

            <PrismaTable
                {...tableProps}
                onRow={({ id, complianceScanStatus }) => ({
                    className: 'cursor-pointer',
                    onClick() {
                        if (complianceScanStatus) {
                            navigate({
                                to: '/console/$orgId/tools/website-checker/report/$id',
                                params: { orgId: org.id, id },
                            });
                        } else {
                            setViewDrawerAction(['view', id]);
                        }
                    },
                })}
            />

            <ComplianceCheckDrawer
                action={complianceCheckAction}
                onClose={() => {
                    setComplianceCheckAction(['none']);
                    void refetch();
                }}
                onComplete={() => void refetch()}
                reuseScrapedData={reuseScrapedData}
            />

            <ViewScrapedDataDrawer action={viewDrawerAction} onClose={() => setViewDrawerAction(['none'])} />

            <ReAnalysisModal
                action={reAnalysisModalAction}
                onClose={() => setReAnalysisModalAction(['none'])}
                onChoose={(choice: ReAnalysisChoice) => {
                    if (reAnalysisModalAction[0] === 'choose') {
                        const { scrapedDataId, websiteUrl } = reAnalysisModalAction[1];
                        setReuseScrapedData(choice === 'reuse');
                        setComplianceCheckAction(['re-analyse', { scrapedDataId, websiteUrl }]);
                    }
                }}
            />
        </PageController>
    );
}
